// src/components/kennels/KennelGridStyles.tsx
import { Box, Typography, Tabs, Tab } from '@mui/material';
import { styled, darken } from '@mui/material/styles';
import { colors } from '../../theme/theme';

export const DATE_COL_WIDTH = 100;
export const KENNEL_COL_WIDTH = 62;
export const ROW_HEIGHT = 28;
export const HEADER_HEIGHT = 36;

export const GridWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'hidden',
});

export const ControlsBar = styled(Box)({
  backgroundColor: colors.primary.main,
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  paddingLeft: 16,
  paddingRight: 16,
  paddingTop: 8,
  paddingBottom: 8,
  flexShrink: 0,
  overflowX: 'auto',
  WebkitOverflowScrolling: 'touch',
  scrollbarWidth: 'none' as const,
  '&::-webkit-scrollbar': { display: 'none' },
});

export const DatePickerWrapper = styled(Box)({
  width: 320,
});

export const GridScroller = styled(Box)({
  flex: 1,
  overflow: 'auto',
  position: 'relative',
});

export const StickyHeaderRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'totalWidth',
})<{ totalWidth: number }>(({ theme, totalWidth }) => ({
  display: 'flex',
  position: 'sticky',
  top: 0,
  zIndex: 10,
  color: theme.palette.grey[50],
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.tertiary.main
      : theme.palette.tertiary.main,
  borderBottom: `1px solid ${theme.palette.grey[900]}`,
  minWidth: totalWidth,
}));

export const StickyDateCorner = styled(Box)(({ theme }) => ({
  width: DATE_COL_WIDTH,
  minWidth: DATE_COL_WIDTH,
  height: HEADER_HEIGHT,
  position: 'sticky',
  left: 0,
  zIndex: 11,
  borderBottom: `1px solid ${theme.palette.grey[900]}`,
  color: theme.palette.grey[50],
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.tertiary.main
      : theme.palette.tertiary.main,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  borderRight: `1px solid ${theme.palette.grey[900]}`,
}));

export const StyledTabs = styled(Tabs)(({ theme }) => ({
  flexShrink: 0,
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.primary.light,
}));

export const StyledTab = styled(Tab)(({ theme }) => ({
  color: theme.palette.primary.dark,
  '&.Mui-selected': {
    // color: theme.palette.primary.dark,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.grey[50],
    // fontWeight: 700,
  },
}));

export const KennelHeaderCell = styled(Box)(({ theme }) => ({
  width: KENNEL_COL_WIDTH,
  minWidth: KENNEL_COL_WIDTH,
  height: HEADER_HEIGHT,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  borderRight: `1px solid ${theme.palette.grey[200]}`,
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.tertiary.main
      : theme.palette.tertiary.main,
}));

export const DateRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'totalWidth',
})<{ totalWidth: number }>(({ totalWidth }) => ({
  display: 'flex',
  minWidth: totalWidth,
}));

export const StickyDateCell = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isToday',
})<{ isToday: boolean }>(({ theme, isToday }) => ({
  width: DATE_COL_WIDTH,
  minWidth: DATE_COL_WIDTH,
  height: ROW_HEIGHT,
  position: 'sticky',
  left: 0,
  zIndex: 2,
  backgroundColor: isToday
    ? theme.palette.primary.main
    : theme.palette.mode === 'dark'
      ? theme.palette.tertiary.dark
      : theme.palette.primary.light,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  overflow: 'hidden',
  paddingRight: 8,
  borderRight: `1px solid ${theme.palette.grey[900]}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

export const DateLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isToday',
})<{ isToday: boolean }>(({ theme, isToday }) => ({
  fontFamily: 'monospace',
  fontSize: '0.9rem',
  fontWeight: 'bold',
  color: isToday
    ? theme.palette.mode === 'dark'
      ? theme.palette.grey[0]
      : theme.palette.grey[0]
    : theme.palette.mode === 'dark'
      ? theme.palette.grey[0]
      : theme.palette.grey[900],
}));

export const EmptyKennelCell = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isEven',
})<{ isEven?: boolean }>(({ theme, isEven }) => ({
  width: KENNEL_COL_WIDTH,
  minWidth: KENNEL_COL_WIDTH,
  height: ROW_HEIGHT,
  overflow: 'hidden',
  backgroundColor: isEven
    ? theme.palette.mode === 'dark'
      ? colors.grey[900]
      : colors.grey[100]
    : 'transparent',
  borderRight: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.28)' : 'rgba(0,0,0,0.28)'}`,
  borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.28)' : 'rgba(0,0,0,0.28)'}`,
}));

export const BookingCell = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'bookingColor' && prop !== 'isTop' && prop !== 'isBot',
})<{ bookingColor: string; isTop: boolean; isBot: boolean }>(
  ({ theme, bookingColor, isTop, isBot }) => ({
    width: KENNEL_COL_WIDTH,
    minWidth: KENNEL_COL_WIDTH,
    height: ROW_HEIGHT,
    backgroundColor: isTop ? darken(bookingColor, 0.3) : bookingColor,
    borderLeft: '1px solid rgba(0,0,0,0.55)',
    borderRight: '1px solid rgba(0,0,0,0.55)',
    borderTop: isTop ? '1px solid rgba(0,0,0,0.55)' : 'none',
    borderBottom: isBot ? '1px solid rgba(0,0,0,0.55)' : 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    cursor: 'pointer',
  })
);

export const CellLabel = styled(Typography)({
  color: '#fff',
  fontWeight: 'bold',
  fontSize: '1.25rem',
  lineHeight: 1,
  userSelect: 'none',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  paddingLeft: 2,
  paddingRight: 2,
});

export const CellMidChar = styled(Typography)({
  color: '#fff',
  fontSize: '1.25rem',
  lineHeight: 1,
  userSelect: 'none',
});
